import { useMutation } from "@tanstack/react-query";
import { DownloadCloud, ImageDown, PencilLine, RefreshCw, Wrench } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/overlays";
import { announceJob, JobProgress } from "@/features/jobs";
import { canWrite, isAdmin, useRole } from "@/features/moderation/hooks";
import { api } from "@/lib/api/client";
import { liveEvents } from "@/lib/api/events";
import { invalidate } from "@/lib/api/query-client";
import { fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { toastFailure } from "@/lib/reauth";
import type { ChannelAccount, Job, Post } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Reload from Instagram (ALP-77): post page, post quick panel, posts table selection and per account. Every reload is a
 * `post_reload` job (jobs center + tray); when it ends the post caches, versions and comments are refetched.
 * Plus media refresh (account pictures) and the org-level media repair (admins).
 */

const POST_KEYS = ["post", "posts", "postVersions", "comments", "commentDetail", "inbox", "jobs"] as const;

/** Run `cb` once the job reaches a terminal state (SSE), or right away when it already did. */
export function onJobDone(job: Pick<Job, "id" | "status">, cb: (status: string) => void): () => void {
  const terminal = (s: string) => s === "succeeded" || s === "failed" || s === "cancelled";
  if (terminal(job.status)) {
    cb(job.status);
    return () => {};
  }
  const off = liveEvents.on((e) => {
    if (e.type === "operation.updated" && e.operationId === job.id && terminal(e.status)) {
      off();
      cb(e.status);
    }
  });
  return off;
}

export const postIsOnChannel = (p: Pick<Post, "publications">) => p.publications.some((x) => x.status === "published" && !!x.externalId);

type ReloadArgs = { postIds?: string[]; accountId?: string; single?: string };

/** Start a reload job (single post → reloadPost, selection / account / all → reloadPosts). */
export function useReloadPosts(opts: { onStarted?: (job: Job) => void; onFinished?: (status: string) => void } = {}) {
  const { t } = useT();
  return useMutation({
    mutationFn: (a: ReloadArgs) =>
      a.single ? api("reloadPost", { params: { postId: a.single }, body: {} }) : api("reloadPosts", { body: { ...(a.postIds ? { postIds: a.postIds } : {}), ...(a.accountId ? { accountId: a.accountId } : {}), includeComments: true } }),
    onSuccess: (job) => {
      announceJob(job);
      opts.onStarted?.(job);
      onJobDone(job, (status) => {
        void invalidate(...POST_KEYS);
        opts.onFinished?.(status);
      });
    },
    onError: (e) => toastFailure(e, t("media.reload.failed")),
  });
}

/** "Reload from Instagram" for one post, with the job's live progress inline while it runs. */
export function ReloadPostButton({ post, size = "sm", className }: { post: Post; size?: "sm" | "xs"; className?: string }) {
  const { t } = useT();
  const role = useRole();
  const [jobId, setJobId] = useState<string | null>(null);
  const reload = useReloadPosts({ onStarted: (j) => setJobId(j.id), onFinished: () => setTimeout(() => setJobId(null), 1200) });
  if (!postIsOnChannel(post)) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Tooltip content={<span className="block max-w-[260px]">{t("media.reload.hint")}</span>}>
        <Button
          size={size}
          variant="secondary"
          icon={<RefreshCw className={cn((reload.isPending || jobId) && "animate-spin motion-reduce:animate-none")} />}
          loading={reload.isPending}
          disabled={!canWrite(role) || !!jobId}
          onClick={() => reload.mutate({ single: post.id })}
          data-testid="reload-post"
        >
          {t("media.reload.action")}
        </Button>
      </Tooltip>
      {jobId && (
        <div className="w-[220px]" data-testid="reload-post-progress">
          <JobProgress jobId={jobId} compact />
        </div>
      )}
    </div>
  );
}

/** Small "Edited on Instagram" badge (post.editedOnChannelAt). */
export function EditedOnInstagramBadge({ post, size }: { post: Pick<Post, "editedOnChannelAt">; size?: "xs" }) {
  const { t } = useT();
  if (!post.editedOnChannelAt) return null;
  return (
    <span title={t("media.edited.hint", { when: fmtRelative(post.editedOnChannelAt) })} data-testid="edited-on-instagram">
      <Badge tone="violet" size={size}>
        <PencilLine className="size-3" aria-hidden />
        {t("media.edited.badge")}
      </Badge>
    </span>
  );
}

/** Settings → Channels: reload the account's posts and re-copy its media + profile pictures. */
export function AccountMediaActions({ account }: { account: Pick<ChannelAccount, "id" | "username" | "status"> }) {
  const { t } = useT();
  const role = useRole();
  const reload = useReloadPosts();
  const refresh = useMutation({
    mutationFn: () => api("refreshAccountMedia", { params: { accountId: account.id }, body: {} }),
    onSuccess: (job) => {
      announceJob(job);
      onJobDone(job, () => void invalidate("accounts", "posts", "post", "contacts", "commenters", "inbox"));
    },
    onError: (e) => toastFailure(e, t("media.refresh.failed")),
  });
  if (account.status === "disconnected" || !canWrite(role)) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="account-media-actions">
      <Tooltip content={<span className="block max-w-[260px]">{t("media.reload.hint")}</span>}>
        <Button size="sm" variant="secondary" icon={<DownloadCloud />} loading={reload.isPending} onClick={() => reload.mutate({ accountId: account.id })} data-testid="reload-account-posts">
          {t("media.reload.account", { user: account.username })}
        </Button>
      </Tooltip>
      <Tooltip content={<span className="block max-w-[260px]">{t("media.refresh.picturesHint")}</span>}>
        <Button size="sm" variant="ghost" icon={<ImageDown />} loading={refresh.isPending} onClick={() => refresh.mutate()} data-testid="refresh-account-media">
          {t("media.refresh.pictures")}
        </Button>
      </Tooltip>
    </div>
  );
}

/** Org admins: copy every channel image not in storage yet (job media_repair). */
export function RepairOrgMediaButton({ className }: { className?: string }) {
  const { t } = useT();
  const role = useRole();
  const repair = useMutation({
    mutationFn: () => api("repairOrgMedia", { body: {} }),
    onSuccess: (job) => {
      announceJob(job);
      onJobDone(job, () => void invalidate(...POST_KEYS, "accounts", "contacts"));
    },
    onError: (e) => toastFailure(e, t("media.repair.failed")),
  });
  if (!isAdmin(role)) return null;
  return (
    <Tooltip content={<span className="block max-w-[260px]">{t("media.repair.orgHint")}</span>}>
      <Button size="sm" variant="secondary" icon={<Wrench />} loading={repair.isPending} onClick={() => repair.mutate()} className={className} data-testid="repair-org-media">
        {t("media.repair.org")}
      </Button>
    </Tooltip>
  );
}

